/**
 * Capture Callbacks
 *
 * Callback functions to be invoked from calculation code.
 * These are the minimal integration points in the core calculators.
 */

import type { RevenueDistributionHelperMap } from '~/engine/processing/engines/distribution-engine/types';
import type { BondAllocation } from '~/engine/processing/engines/revenue-engine/distribution-types';
import type { Candidate, CashFlow, Configuration } from '~/types';
import type { DebugExtractionContext, PrincipalDecisionContext } from '../types';
import { DebugCapture } from './debug-capture';

/**
 * Capture payment state during revenue distribution
 *
 * Call this after each payment is processed in the backwards loop.
 * This function has zero overhead when capture is not active.
 *
 * @param candidate - The candidate being processed
 * @param cashFlow - The current cash flow record
 * @param paymentIndex - Index in the cash flows array
 * @param helperMap - Revenue distribution helper map (optional)
 * @param configuration - The configuration
 * @param decisionContext - Decision context captured at calculation time (optional)
 * @param bondAllocations - Calculated bond allocations for this payment (optional)
 */
export function capturePaymentState(
  candidate: Candidate,
  cashFlow: CashFlow,
  paymentIndex: number,
  helperMap: RevenueDistributionHelperMap | undefined,
  configuration: Configuration,
  decisionContext?: PrincipalDecisionContext,
  bondAllocations?: readonly BondAllocation[]
): void {
  // Fast path: exit immediately if not capturing this candidate
  if (!DebugCapture.isCapturing(candidate.id)) {
    return;
  }

  // Build extraction context
  const context: DebugExtractionContext = {
    candidate,
    configuration,
    cashFlow,
    paymentIndex,
    helperMap,
    bondAllocations,
    revenueMap: candidate.revenueMap,
    bondSeries: configuration.bondSeries,
    decisionContext,
  };

  // Record the payment
  DebugCapture.recordPayment(context);
}

/**
 * Update capture summary with candidate results
 *
 * Call this after processing completes to include viability info.
 */
export function updateCaptureWithResults(candidate: Candidate): void {
  if (!DebugCapture.isCapturing(candidate.id)) {
    return;
  }

  const { results } = candidate;
  if (results) {
    DebugCapture.updateSummaryFromResults(results.isViable, results.failureMessages);
  }
}
